import { useEffect } from 'react';
import { Link } from 'react-router-dom';

const STEPS: { title: string; body: string; entity?: string; entityLabel?: string }[] = [
  {
    title: 'Where invoice settings live',
    body: 'Settings → Invoice holds the issuer details rendered at the top of every generated invoice PDF — your "from" block. Everything here is optional: fill in what you want shown and leave the rest blank, and the renderer simply omits anything empty. These settings are workspace-wide, so they apply to every invoice you issue. Only Admins and Owners can change them.',
    entity: 'invoice',
    entityLabel: 'Invoice',
  },
  {
    title: 'Add your logo and company details',
    body: 'Upload a logo — it\'s resized and stored automatically, and you can remove it anytime. Set the legal company name (it defaults to your workspace name if left blank), your postal address, and the contact details you want on the invoice: billing email, phone, and website.',
    entity: 'tenant',
    entityLabel: 'Tenant',
  },
  {
    title: 'Set your tax and registration identifiers',
    body: 'Enter your tax / VAT registration number and, if you like, relabel it — the tax label defaults to "Tax ID" but you can set it to "VAT", "EIN", or whatever fits your jurisdiction. Add a company registration / incorporation number too if you need it on the document.',
  },
  {
    title: 'Choose payment terms and currency',
    body: 'Under Settings → General, set your reporting currency and default payment terms — a number of days (30 by default) or Due on receipt. Payment terms drive each invoice\'s due date — issue date plus the term, or the issue date itself for due-on-receipt. You can override the term for an individual client on their record, so a customer on net-45 gets net-45, another gets due-on-receipt, while everyone else stays on your default.',
    entity: 'client',
    entityLabel: 'Client',
  },
  {
    title: 'Add your payment information',
    body: 'Settings → Invoice → Payment Information is where you tell customers how to pay you. Add a bank account per region and currency — account name, account number, IBAN, SWIFT/BIC, routing number, branch code, and bank address — plus a free-form block of payment instructions shown on every invoice regardless of currency. Scoping by region lets a workspace that sells into several markets offer a region-appropriate account for the same currency; leave the region set to "All regions" for a region-agnostic default. Every generated invoice PDF renders a "Payment details" section automatically, picking the account that matches the recipient\'s region and the invoice currency and falling back to a region-agnostic account for that currency; the section stacks dynamically below your notes so long instructions never overlap. Like the rest of invoice settings, only Admins and Owners can change it, and it applies workspace-wide.',
    entity: 'tenant',
    entityLabel: 'Tenant',
  },
  {
    title: 'How invoice numbers are formed',
    body: 'Numbers are assigned automatically and never collide. The format is PREFIX-SEQUENCE — for example ACME-00042: a prefix and a running number. The prefix is the client\'s configured invoice prefix when set (any trailing separator is trimmed, so "INV-ACME-" reads cleanly); otherwise Revvly falls back to a short slug from the client name. The sequence counts up across every invoice that client is ever issued and never resets, so 00042 is their forty-second invoice. You don\'t assign numbers by hand — Revvly reserves the next one atomically as each invoice is generated. Onboarding a client who already has invoice history elsewhere? Set "Next invoice number" on their record and the sequence picks up from there.',
    entity: 'invoice',
    entityLabel: 'Invoice',
  },
  {
    title: 'Preview the result',
    body: 'Every generated invoice produces a PDF that\'s stored and downloadable from the invoice itself. Issue or open any invoice to see your header, logo, tax details, and terms applied — change a setting and the next generated PDF reflects it. Existing PDFs keep the details they were rendered with until you regenerate them.',
  },
  {
    title: 'Regenerate an invoice after a change',
    body: 'Changed a client\'s payment terms, a price, or your payment details and need an already-issued invoice to reflect it? The "Regenerate invoice" action — on the invoice page and on its Data tab — rebuilds a Pending or Overdue subscription or renewal invoice from the subscription\'s current pricing and the client\'s current payment terms, then re-renders the PDF. Line items, amount, discounts, due date, and estimated payment date are all refreshed; the original issue date is kept. Settled invoices (Paid, Failed, Cancelled) are never touched, and regenerating never resets a usage counter or consumes a discount\'s remaining renewals. If the rebuild leaves the invoice at zero — a discount now covers the whole charge, say — it is marked Paid, because there is nothing left to collect. Because it overwrites the invoice, the action is behind a confirmation.',
    entity: 'invoice',
    entityLabel: 'Invoice',
  },
];

export function CustomizeInvoicesGuide() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Customize your invoices — Revvly Docs';
  }, []);

  return (
    <div className="tn-page">
      <nav className="dp-crumbs" aria-label="Breadcrumb">
        <Link to="/docs/guides">Guides</Link>
        <span aria-hidden="true">›</span>
        <span className="dp-crumb-current">Customize your invoices</span>
      </nav>

      <header className="dp-header">
        <span className="rv-eyebrow-mini">Guide</span>
        <h1>
          Customize your <em>invoices</em>.
        </h1>
        <p className="dp-oneliner">
          Put your logo, company and tax details, banking and payment information, payment terms,
          and currency on every invoice — and see how Revvly numbers them for you.
        </p>
      </header>

      {STEPS.map((step, i) => (
        <div key={i} className="dp-section">
          <h2>
            <span style={{ color: 'var(--rv-fg-dark-5)', fontWeight: 400, marginRight: 12 }}>
              {String(i + 1).padStart(2, '0')}
            </span>
            {step.title}
          </h2>
          <p className="dp-plain">{step.body}</p>
          {step.entity && (
            <ul className="dp-connects" style={{ marginTop: 14 }}>
              <li>
                <Link to={`/docs/concepts/${step.entity}`} style={{ color: 'inherit', textDecoration: 'none' }}>
                  {step.entityLabel} concept ›
                </Link>
              </li>
            </ul>
          )}
        </div>
      ))}
    </div>
  );
}
